/** The Documents place, Library tab - every document EmitIQ holds, as one gridded table (ADR-0212).
 *
 * A title, four tiles from the server's own counts, one line of filters and one table, with Upload and
 * Import in the header. This is the old Evidence library with the same honesty and a different shape:
 *
 * - **Every document, oldest first, in an order the screen says** (`library-order`, gate 18): the one
 *   that has waited longest is found first, and the order is requested from the server and not
 *   trusted from it.
 * - **A search is the database's.** The questions (a word, a kind, a site, a stage, a period, whether a
 *   figure in force cites it) are sent to `GET /evidence/`, which holds every row, so the 201st
 *   document is as reachable as the first. With none asked the table is the whole library, a page at
 *   a time.
 * - **It never passes a page off as the library.** `library-count` carries the server's total beside
 *   what arrived, "N not listed" has the way to read the next page and all of them, and a search with
 *   no match is told apart from a library with nothing in it and from a read that failed.
 * - **The tiles are the server's counts of the whole library**, not a count of the pages held, so a
 *   library of five hundred does not read as fifty.
 *
 * Nothing here moves a document. A row opens the document's review; the Source column says how it
 * arrived and is derived from the row, never stored.
 *
 * **The table power features, honestly scoped to a paged list.** The filters are chips mirrored in the
 * address; the saved views, density, column chooser, quick-peek and Export come from `useGridPower`.
 * Three things differ from a table that holds every row: there is **no client sort and no grouping**
 * (ordering or grouping the page held would present a page as the library, so the library stays in
 * the order the server gives and the screen states it); "Select all N matching" loads the rest of the
 * library before it selects, so what is ticked is what the bulk bar says; and the bulk bar offers
 * Export only - the page supplies no action, because moving a document is the review's act.
 */

import { Files } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../../api/auth";
import {
  LIBRARY_NEXT_PAGE,
  isFiltered,
  useDocumentLibrary,
  useDocumentSearch,
  useFacilities,
} from "../../api/evidence";
import type { DocumentRow, DocumentState, LibraryFilters } from "../../api/evidence";
import { stateCount, useDocumentCounts } from "../../api/processing";
import { ReadFailed } from "../../components/Loaded";
import {
  AddImportActions,
  DashboardBand,
  EmptyAction,
  EmptyState,
  FilterLine,
  GridTable,
  InfoPopover,
  RowMenu,
  SourceChip,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
  originWord,
  useGridPower,
  useUrlFilters,
} from "../../components/ui";
import type { FilterDef, GridColumn, SavedView } from "../../components/ui";
import { KIND_LABEL, kindLabel } from "../../lib/documentKinds";
import { originOfDocument } from "../../lib/documentOrigin";
import { fileSize, periodShort, quantity, shortDate } from "../../lib/format";
import { mayEnterData } from "../../shell/routeTable";
import { ScreenFrame } from "../ScreenFrame";
import { recordMenuItem } from "../RecordLink";
import { keepSearch } from "../search";
import { stageChipOf } from "../upload/stageChip";
import { useScreen } from "../useScreen";
import { useDocumentCharts } from "../bands/DocumentCards";
import { DocumentNote, noteText } from "./DocumentNote";
import { useMoreOf } from "./paging";
import styles from "./Library.module.css";

const STAGES: ReadonlyArray<{ value: DocumentState; label: string }> = [
  { value: "validated", label: "Checked" },
  { value: "awaiting_review", label: "Needs review" },
  { value: "verified", label: "Accepted" },
  { value: "quarantined", label: "Refused" },
  { value: "rejected", label: "Set aside" },
  { value: "superseded", label: "Replaced" },
  { value: "extracting", label: "Reading" },
  { value: "received", label: "Received" },
];

/** The views the library ships, locked. Each is a stage the server already filters by. */
const stageView = (id: string, name: string, stage: DocumentState): SavedView => ({
  id,
  name,
  config: { filters: { search: "", values: { stage: [stage] } }, sorts: [], hidden: [], groupBy: null, pinned: [] },
});
const STARTER_VIEWS: readonly SavedView[] = [
  stageView("review", "Needs review", "awaiting_review"),
  stageView("refused", "Refused", "quarantined"),
];

const count = (value: number | undefined): string => (value === undefined ? "–" : quantity(String(value)));

function periodOf(document: DocumentRow): string {
  return document.period_start && document.period_end
    ? `${shortDate(document.period_start)} – ${shortDate(document.period_end)}`
    : "period not established";
}

export function LibraryTab() {
  const screen = useScreen();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const counts = useDocumentCounts();
  const charts = useDocumentCharts();
  const sites = useFacilities();
  // The filters live in the address (`?f_stage=...`): a filtered library is a link.
  const [filters, setFilters] = useUrlFilters();

  const pick = (id: string): string | undefined => filters.values[id]?.[0];
  const period = pick("period");
  const asked: LibraryFilters = {
    ...(filters.search.trim() ? { q: filters.search.trim() } : {}),
    ...(pick("kind") ? { kind: pick("kind") as NonNullable<LibraryFilters["kind"]> } : {}),
    ...(pick("site") ? { facilityId: pick("site") } : {}),
    ...(pick("stage") ? { state: pick("stage") as DocumentState } : {}),
    ...(pick("figures") ? { figures: pick("figures") as "linked" | "unlinked" } : {}),
    ...(period === "none" ? { periodUnknown: true } : {}),
    ...(period === "window" && screen.window
      ? { periodFrom: screen.window.period_start, periodTo: screen.window.period_end }
      : {}),
  };
  const filtered = isFiltered(asked);
  const whole = useDocumentLibrary();
  const search = useDocumentSearch(asked);
  const heldMore = useMoreOf(whole);
  const searchMore = useMoreOf(search);

  const read = filtered ? search : whole;
  const data = filtered ? search.data : whole.data;
  const more = filtered ? searchMore : heldMore;
  const documents = [...(data?.rows ?? [])].sort((a, b) => a.id.localeCompare(b.id));
  const total = data?.total ?? 0;
  const reportedTotal = data?.reportedTotal ?? null;
  const failure = read.error && !read.isFetchNextPageError ? read.error : null;
  const waiting = read.isPending && !read.isError;
  //: Not listed means there is a page left to read: `total - listed` alone would also count what an
  //: Auditor's engagement keeps from them, which no button could ever reduce.
  const unlisted = more.available ? Math.max(total - documents.length, 0) : 0;
  const nextCount = Math.min(unlisted, LIBRARY_NEXT_PAGE);
  const busy = more.loading || more.loadingAll;
  // Nothing has ever been uploaded: no filters, saved views or tools to work on, and no chart to draw.
  const nothingYet = !filtered && !waiting && !failure && reportedTotal === 0 && documents.length === 0;

  const defs: FilterDef[] = [
    {
      id: "kind",
      label: "Type",
      mode: "single",
      allLabel: "Any type",
      options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
    },
    // A failed read of the sites is not a workspace with none: the filter is withheld rather than
    // offering a choice of nobody, and "No site" would then be the only option on a list we do not have.
    ...(sites.error || !sites.data
      ? []
      : [
          {
            id: "site",
            label: "Site",
            mode: "single" as const,
            allLabel: "Any site",
            options: [
              ...sites.data.map((site) => ({ value: site.id, label: site.name })),
              { value: "none", label: "No site" },
            ],
          },
        ]),
    {
      id: "stage",
      label: "Stage",
      mode: "single",
      allLabel: "Any stage",
      // The server's own count of the whole library at each stage, where it has answered.
      options: STAGES.map((stage) => {
        const held = stateCount(counts.data, stage.value);
        return held === undefined ? stage : { ...stage, count: held };
      }),
    },
    {
      id: "period",
      label: "Period",
      mode: "single",
      allLabel: "Any period",
      options: [
        ...(screen.window ? [{ value: "window", label: "This reporting window" }] : []),
        { value: "none", label: "No period established" },
      ],
    },
    {
      id: "figures",
      label: "Figures",
      mode: "single",
      allLabel: "Any",
      options: [
        { value: "linked", label: "Cited by a figure" },
        { value: "unlinked", label: "Cited by no figure" },
      ],
    },
  ];

  const power = useGridPower({
    place: "documents",
    userKey: me?.user_id,
    filters,
    onFiltersChange: setFilters,
    filterDefs: defs,
    resultNoun: "documents",
    starterViews: STARTER_VIEWS,
    exportName: "documents",
  });

  const open = (document: DocumentRow) =>
    void navigate({
      to: "/evidence/$documentId",
      params: { documentId: document.id },
      search: { method: screen.method },
    });

  const columns: GridColumn<DocumentRow>[] = [
    {
      id: "file",
      header: "File",
      minWidth: "180px",
      groupable: false,
      value: (document) => document.original_filename ?? "Untitled document",
      cell: (document) => (
        <span
          className={styles.file}
          data-testid="library-row"
          data-document-id={document.id}
          data-document-name={document.original_filename ?? ""}
        >
          <span className={styles.name} title={document.original_filename ?? undefined}>
            {document.original_filename ?? "Untitled document"}
          </span>
          <span className={styles.meta}>{fileSize(document.byte_size)}</span>
        </span>
      ),
    },
    { id: "type", header: "Type", width: "108px", groupable: false, value: (document) => kindLabel(document.kind), cell: (document) => kindLabel(document.kind) },
    {
      id: "site",
      header: "Site",
      width: "112px",
      groupable: false,
      value: (document) => document.facility_hint_name ?? "",
      cell: (document) => document.facility_hint_name ?? <span className={styles.none}>–</span>,
    },
    {
      id: "period",
      header: "Period",
      width: "104px",
      groupable: false,
      value: periodOf,
      cell: (document) =>
        document.period_start && document.period_end ? periodShort(document.period_start, document.period_end) : periodOf(document),
    },
    {
      id: "stage",
      header: "Stage",
      width: "116px",
      groupable: false,
      value: (document) => stageChipOf(document).word,
      cell: (document) => {
        const chip = stageChipOf(document);
        return (
          <span data-document-state={document.state}>
            <StatusChip tone={chip.tone} size="sm">
              {chip.word}
            </StatusChip>
          </span>
        );
      },
    },
    {
      id: "vendor",
      header: "Vendor",
      width: "96px",
      groupable: false,
      value: (document) => document.vendor_name ?? "",
      cell: (document) => document.vendor_name ?? <span className={styles.none}>–</span>,
    },
    {
      id: "figures",
      header: "Figures",
      width: "76px",
      numeric: true,
      groupable: false,
      value: (document) =>
        document.state === "verified" && document.linked_figures !== null && document.linked_figures !== undefined
          ? document.linked_figures
          : "",
      cell: (document) => {
        const figures = document.linked_figures;
        // Said only for an accepted document: a document nobody has accepted has produced nothing,
        // and "none" would be true of every one in the queue and mean nothing. `null` is *not
        // counted*, and prints nothing - never "none".
        if (document.state !== "verified" || figures === null || figures === undefined) {
          return <span className={styles.none}>–</span>;
        }
        return (
          <span data-testid="library-figures" data-figures={figures}>
            {figures}
          </span>
        );
      },
    },
    {
      id: "source",
      header: "Source",
      width: "116px",
      groupable: false,
      value: (document) => originWord(originOfDocument(document.source)),
      cell: (document) => <SourceChip origin={originOfDocument(document.source)} />,
    },
    {
      id: "note",
      header: "Note",
      width: "160px",
      groupable: false,
      value: noteText,
      cell: (document) => <DocumentNote document={document} />,
    },
    {
      // The way to the record's notes, flags and approvals is a menu on the row, not a column: a link
      // that says "Notes, flags and approvals" held 172px of every row open (U6, 2026-11-03).
      id: "record",
      header: "Actions",
      headerHidden: true,
      width: "44px",
      groupable: false,
      hideable: false,
      cell: (document) => (
        <RowMenu
          name={document.original_filename ?? "this document"}
          items={[
            recordMenuItem({
              recordId: document.id,
              kind: "document",
              subject: document.original_filename ?? "this document",
            }),
          ]}
        />
      ),
    },
  ];

  // **"0 of 0" is a count, and a failed read has none** (G-P9-16): beside the failure notice it read as
  // a library with nothing in it. A read that failed says so in the count's own place.
  const status = failure
    ? "Not read"
    : waiting
      ? "Searching"
      : `${quantity(String(documents.length))} of ${quantity(String(total))}`;

  return (
    <ScreenFrame screen={screen} title="Documents">
      <TablePage
        icon={Files}
        title="Documents"
        label="Library"
        actions={
          <AddImportActions
            role={me?.role}
            addLabel="Upload"
            onAdd={() => void navigate({ to: "/upload", search: keepSearch })}
            onImport={() => void navigate({ to: "/bulk", search: keepSearch })}
          />
        }
        band={
          <DashboardBand
            label="Documents dashboard"
            charts={charts}
            tiles={
          counts.error ? (
            <ReadFailed of="The document counts" error={counts.error} onRetry={() => void counts.refetch()} />
          ) : (
          <TileStrip label="Documents">
            <Tile label="Stored" value={count(counts.data?.documents)} loading={counts.isPending} testId="tile-stored" />
            <Tile
              label="To review"
              value={count(stateCount(counts.data, "awaiting_review"))}
              loading={counts.isPending} testId="tile-review"
            />
            <Tile label="Accepted" value={count(stateCount(counts.data, "verified"))} loading={counts.isPending} testId="tile-accepted" />
            <Tile label="Refused" value={count(stateCount(counts.data, "quarantined"))} loading={counts.isPending} testId="tile-refused" />
          </TileStrip>
          )
            }
          />
        }
        nothingYet={nothingYet}
        filters={
          nothingYet ? undefined : (
          <FilterLine
            power={power}
            searchLabel="Search the library"
            actions={
              <>
                <span
                  className={styles.status}
                  role="status"
                  data-tone="neutral"
                  data-testid="library-count"
                  data-listed={documents.length}
                  data-matching={documents.length}
                  data-stale={String(waiting || read.isFetching)}
                  data-total={total}
                  data-reported-total={reportedTotal ?? "unknown"}
                  data-truncated={String(unlisted > 0)}
                  data-filtered={String(filtered)}
                >
                  {status}
                </span>
                <span className={styles.status} data-tone="neutral" data-testid="library-order" data-order="oldest-first">
                  Oldest first
                </span>
                <InfoPopover infoKey="documents.library" />
                <InfoPopover infoKey="documents.source" />
              </>
            }
          />
          )
        }
        table={
          failure ? (
            <ReadFailed of="The evidence library" error={failure} onRetry={() => void read.refetch()} />
          ) : (
            <>
              {sites.error ? (
                <ReadFailed of="The list of sites" error={sites.error} onRetry={() => void sites.refetch()} />
              ) : null}
              <GridTable
                caption="Documents, oldest first"
                testId="library-list"
                columns={columns}
                rows={documents}
                rowKey={(document) => document.id}
                rowLabel={(document) => document.original_filename ?? "document"}
                selectable={mayEnterData(me?.role)}
                power={power}
                renderPeek={(document) => <DocumentPeek document={document} />}
                totalMatching={total}
                onSelectAllMatching={more.available ? more.all : undefined}
                onRowOpen={open}
                loading={waiting}
                empty={
                  filtered ? (
                    <EmptyState title="No document answers that question" compact testId="library-no-match" />
                  ) : (
                    <EmptyState
                      icon={Files}
                      title={reportedTotal === 0 ? "No documents yet" : "No documents were returned"}
                      compact
                      testId="library-empty"
                      action={
                        <EmptyAction
                          label="Upload a bill"
                          allowed={me?.role === undefined ? undefined : mayEnterData(me.role)}
                          reason="A Contributor, Approver or Admin uploads documents."
                          to="/upload"
                          search={keepSearch}
                          plus={false}
                          testId="library-empty-upload"
                        />
                      }
                    >
                      {reportedTotal === 0 ? "Every bill you upload is stored here." : null}
                    </EmptyState>
                  )
                }
              />
              {unlisted > 0 ? (
                <div className={styles.more} data-testid="library-truncated" role="group" aria-label="More documents">
                  <span data-tone="warn" className={styles.status}>
                    {unlisted} not listed
                  </span>
                  <button
                    className={styles.button}
                    type="button"
                    data-testid="library-more"
                    disabled={busy}
                    onClick={more.next}
                  >
                    {more.loading && !more.loadingAll ? "Reading…" : `Show the next ${nextCount}`}
                  </button>
                  {unlisted > nextCount ? (
                    <button
                      className={styles.button}
                      type="button"
                      data-testid="library-more-all"
                      disabled={busy}
                      onClick={more.all}
                    >
                      {more.loadingAll ? "Reading…" : `Load all ${unlisted} remaining`}
                    </button>
                  ) : null}
                  {more.error ? (
                    <ReadFailed
                      of="The next page of the library"
                      error={more.error}
                      onRetry={more.next}
                    />
                  ) : null}
                </div>
              ) : null}
            </>
          )
        }
      />
    </ScreenFrame>
  );
}

/** The quick-peek panel's body for a document: what the row says, in the order the review reads it.
 * The file itself and its text are the review's (`SourceText`); nothing here fetches. */
function DocumentPeek({ document }: { document: DocumentRow }) {
  const chip = stageChipOf(document);
  const facts: Array<[string, ReactNode]> = [
    ["Type", kindLabel(document.kind)],
    ["Site", document.facility_hint_name ?? "–"],
    ["Period", periodOf(document)],
    [
      "Stage",
      <StatusChip key="stage" tone={chip.tone} size="sm">
        {chip.word}
      </StatusChip>,
    ],
    ["Vendor", document.vendor_name ?? "–"],
    ["Size", fileSize(document.byte_size)],
    ["Source", <SourceChip key="source" origin={originOfDocument(document.source)} />],
  ];
  return (
    <dl className={styles.peekFacts} data-testid="document-peek">
      {facts.map(([label, value]) => (
        <div key={label} className={styles.peekFact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
